<script setup lang="ts">
import {
  Modal,
  RadioInputList,
  CustomLink,
  LinkColor,
  LinkSize,
  LinkType,
  type ILink,
  type IRadioInputListChangeEvent
} from "@frontiers-vue3/public-pages";

import type {
  IDialogSubmitManuscript,
  IDialogSectionDetails
} from "./IDialogSubmitManuscript";
import type { IJournal } from "~/types";

const props = defineProps<IDialogSubmitManuscript>();
const emit = defineEmits(["close"]);

const route = useRoute();

const isOpen = ref<boolean>(props.isDialogOpen);
const resetRadioInputs = ref<boolean>(false);
const { manuscriptSubmissionDeadline } = useMain().currentResearchTopic;

const MapSectionDetails = (journals: IJournal[]): IDialogSectionDetails[] => {
  if (!journals) return [];

  return journals.flatMap((journal) => {
    const journaltaxonomy = journal.taxonomy.find(
      (t) => t.domainId === journal.defaultDomainId
    );
    return journal.sections && journal.sections.length > 0
      ? journal.sections.map((section) => ({
          choosenSectionId: `${journal.id}-${section.sectionId}`,
          id: journal.id,
          sectionName: journal.title,
          domainId: section.taxonomy[0].domainId,
          fieldId: section.taxonomy[0].fieldId,
          specialtyId: section.taxonomy[0].specialityId,
          subTitle: section.title,
          entityId: section.sectionId,
          entityType: 2,
          publicationTypeId: 1,
          sectionId: section.sectionId
        }))
      : [
          {
            choosenSectionId: `${journal.id}-0`,
            id: journal.id,
            sectionName: journal.title,
            domainId: journaltaxonomy
              ? journaltaxonomy.domainId
              : journal.taxonomy[0].domainId,
            fieldId: journaltaxonomy
              ? journaltaxonomy.fieldId
              : journal.taxonomy[0].fieldId,
            specialtyId: journaltaxonomy
              ? journaltaxonomy.specialityId
              : journal.taxonomy[0].specialityId,
            subTitle: "",
            entityId: journal.id,
            entityType: 1,
            publicationTypeId: 1,
            sectionId: 0
          }
        ];
  });
};
const submitSections = computed((): IDialogSectionDetails[] => {
  return MapSectionDetails(props.participatingJournalsToSubmit);
});

const chosenSection = ref(
  submitSections.value.length === 1
    ? submitSections.value[0].choosenSectionId
    : undefined
);

const closeDialog = (): void => {
  const router = useRouter();
  const query = { ...router.currentRoute.value.query };
  if (query["submitManuscriptVisible"]) {
    delete query["submitManuscriptVisible"];
    router.push({ query });
  }

  if (submitSections.value.length > 1) chosenSection.value = undefined;

  emit("close");
  setTimeout(() => (resetRadioInputs.value = false), 500);
};

const hasMultipleSubmitSections = computed((): boolean => {
  return submitSections.value.length > 1;
});

const getNavigationValues = (): Record<string, unknown> => {
  const fs = submitSections.value.find(
    (fs) => fs.choosenSectionId === chosenSection.value && chosenSection.value
  );
  const entityId = fs?.subTitle && fs.subTitle.trim() ? fs.sectionId : fs?.id;

  return {
    domainId: fs?.domainId,
    fieldId: fs?.fieldId,
    specialtyId: fs?.specialtyId,
    entityType: fs?.entityType,
    entityId
  };
};

watch(
  () => props.isDialogOpen,
  () => {
    resetRadioInputs.value = true;
    isOpen.value = props.isDialogOpen;
  }
);

const onChange = (change: IRadioInputListChangeEvent) => {
  chosenSection.value = change.value.choosenSectionId;
};

const submitButtonLink = computed((): ILink => {
  const nv = getNavigationValues();
  return {
    text: "Submit your manuscript",
    url: `/submission/submit?st=1&tid=${route.params.rtId}&domainId=${nv?.domainId}&fieldId=${nv?.fieldId}&specialtyId=${nv?.specialtyId}&entityType=${nv.entityType}&entityId=${nv.entityId}`
  };
});

const submitYourManuscriptButtonLink = computed((): ILink => {
  const nv = getNavigationValues();
  return {
    text: "Submit your manuscript summary",
    url: `/submission/submit?st=3&tid=${route.params.rtId}&domainid=${nv?.domainId}&fieldid=${nv?.fieldId}&specialtyid=${nv?.specialtyId}&entitytype=${nv?.entityType}&entityid=${nv.entityId}#submission-details`
  };
});

const submitButtonClasses = computed(() => {
  return {
    "Button--disabled": !chosenSection.value && hasMultipleSubmitSections.value
  };
});
</script>
<template>
  <Modal
    :force-show="isOpen"
    class="SubmitIdeaDialog"
    title="Submit your idea"
    heading="p"
    @close="closeDialog">
    <template #body>
      <div v-if="hasMultipleSubmitSections" class="SubmitIdeaDialog__Body">
        <p class="SubmitIdeaDialog__Body__Text">
          Select the journal/section where you want your idea to be submitted:
        </p>
        <RadioInputList
          v-if="resetRadioInputs"
          :options="submitSections"
          data-text="sectionName"
          data-subtext="subTitle"
          name="SectionsToSubmit"
          @change="onChange" />
      </div>

      <div v-else class="SubmitIdeaDialog__Body">
        <p class="SubmitIdeaDialog__Body__Text">
          You will be redirected to our submission process.
        </p>
      </div>
    </template>
    <template #footer>
      <CustomLink
        v-if="new Date(manuscriptSubmissionDeadline) >= new Date()"
        :link="submitYourManuscriptButtonLink"
        :class="submitButtonClasses"
        :type="LinkType.OutlineButton"
        :size="LinkSize.Medium"
        :color="LinkColor.Maincolor" />
      <CustomLink
        :link="submitButtonLink"
        :class="submitButtonClasses"
        :type="LinkType.OutlineButton"
        :size="LinkSize.Medium"
        :color="LinkColor.Maincolor" />
    </template>
  </Modal>
</template>
<style lang="scss">
.Button--disabled {
  pointer-events: none;
  opacity: 0.5;
}
.SubmitIdeaDialog {
  &__Body {
    padding-top: 36px;
    margin-bottom: 32px;
    padding-left: 36px;
    padding-right: 36px;

    &__Text {
      @include f-16-500;
      color: $black;
      margin-bottom: 24px;
    }

    .RadioInputList {
      display: flex;
      flex-wrap: wrap;
      row-gap: 16px;

      &__checkmark {
        min-width: 24px;
        min-height: 24px;
      }

      &__label {
        font-weight: 600;
      }

      &__subLabel {
        font-weight: 500;
      }
    }
  }

  .Modal {
    &__container {
      max-width: 748px;
    }

    &__footer {
      flex-wrap: wrap;
      border-top: 0;

      span {
        flex: 1 1;

        a {
          display: flex;
          text-align: center;
        }
      }
    }
  }
}
</style>
